import React, { useEffect, useState } from "react";
import { Copy, ArrowRight, Landmark, Smartphone, QrCode } from "lucide-react";
import { Input } from "./ui/input";
import { inr } from "../lib/format";
import { GLASS_INPUT, Cta } from "./WalletUI";

// Admin-verified deposit: pick how you paid (Bank / UPI ID / QR), read the house details, then submit amount + UTR.
const TABS = [
  { key: "bank", label: "Bank Details", icon: Landmark, method: "IMPS" },
  { key: "upi", label: "UPI Payment", icon: Smartphone, method: "UPI" },
  { key: "qr", label: "Payment QR Code", icon: QrCode, method: "QR" },
];

function Field({ label, value, testid, onCopy }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">{label}</div>
      <div className="mt-0.5 flex items-center gap-2">
        <span className="font-semibold text-white text-sm break-all tabular-nums select-all">{value || "—"}</span>
        {value && onCopy && (
          <button type="button" onClick={() => onCopy(value, label)} data-testid={`${testid}-copy`}
            className="grid place-items-center h-7 w-7 shrink-0 rounded-lg bg-white/5 ring-1 ring-white/10 text-slate-300 hover:text-[#f5b400] hover:ring-[#f5b400]/50 transition-colors">
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function ManualDeposit({ bank, minDep, max, bonusPct, md, setMd, onSubmit, copyText }) {
  const manual = bank?.manual || {};
  const available = { bank: !!bank?.bank?.accountNumber, upi: !!manual.upi?.upiId, qr: !!manual.qr?.image };
  const first = TABS.find((t) => available[t.key]) || TABS[0];
  const [tab, setTab] = useState(first.key);
  const n = Number(md.amount) || 0;
  const valid = n >= minDep && n <= max && (md.utr || "").trim().length >= 6;

  const pick = (t) => { if (available[t.key]) setTab(t.key); };
  const method = (TABS.find((t) => t.key === tab) || first).method;
  useEffect(() => { setMd((p) => ({ ...p, method })); }, [method, setMd]);  // submitted method always matches the visible tab

  const title = tab === "bank" ? "Bank Details" : tab === "upi" ? "UPI Details" : "Payment QR Details";

  return (
    <div className="px-4 pb-5 pt-4 grid gap-4 md:grid-cols-[180px_1fr]" data-testid="manual-deposit-panel">
      <div className="flex md:flex-col gap-2 overflow-x-auto no-scrollbar" data-testid="manual-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => pick(t)} disabled={!available[t.key]} data-testid={`manual-tab-${t.key}`}
            className={`shrink-0 md:w-full rounded-xl px-4 py-3 text-sm font-bold text-left transition-colors inline-flex items-center gap-2 ${
              tab === t.key ? "bg-[#f5b400] text-black" : available[t.key] ? "bg-white/[0.05] text-white ring-1 ring-white/10 hover:ring-[#f5b400]/50" : "bg-white/[0.03] text-slate-500 ring-1 ring-white/5 cursor-not-allowed"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-4 min-w-0">
        <div className="rounded-2xl bg-[#0b1220] ring-1 ring-white/10 p-2" data-testid="manual-details-card">
          <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 overflow-hidden">
            <div className="bg-[#f5b400] text-black text-center font-display font-extrabold text-base py-2.5 tracking-wide" data-testid="manual-details-title">{title}</div>
            <div className="p-4 grid grid-cols-2 gap-x-4 gap-y-4">
              {tab === "bank" && (<>
                <Field label="Bank Name" value={bank?.bank?.bankName} testid="md-bank-name" />
                <Field label="Account No." value={bank?.bank?.accountNumber} testid="md-bank-account" onCopy={copyText} />
                <Field label="Account Holder Name" value={bank?.bank?.accountName} testid="md-bank-holder" />
                <Field label="Bank IFSC Code" value={bank?.bank?.ifsc} testid="md-bank-ifsc" onCopy={copyText} />
              </>)}
              {tab === "upi" && (<>
                <Field label="UPI ID" value={manual.upi?.upiId} testid="md-upi-id" onCopy={copyText} />
                <Field label="Holder Name" value={manual.upi?.holder} testid="md-upi-holder" />
              </>)}
              {tab === "qr" && (<>
                <Field label="Merchant Name" value={manual.qr?.merchant} testid="md-qr-merchant" />
                <div data-testid="md-qr-image">
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-400">Scan to pay</div>
                  {manual.qr?.image && <img src={manual.qr.image} alt="Payment QR" className="mt-2 w-40 h-40 rounded-lg bg-white p-1 object-contain" />}
                </div>
              </>)}
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <div className="text-sm font-semibold text-slate-200 mb-1.5">Deposit Amount<span className="text-[#f5b400]">*</span></div>
            <Input value={md.amount || ""} onChange={(e) => setMd({ ...md, amount: e.target.value.replace(/[^\d]/g, "") })} placeholder="Enter Amount" inputMode="numeric"
              className={`${GLASS_INPUT} tabular-nums`} data-testid="deposit-amount-input" />
            <div className="mt-1 flex justify-between text-[11px] text-slate-500"><span>Min {inr(minDep)}</span><span>Max {inr(max)}</span></div>
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-200 mb-1.5">UTR No<span className="text-[#f5b400]">*</span></div>
            <Input value={md.utr || ""} onChange={(e) => setMd({ ...md, utr: e.target.value })} placeholder="Transaction ID" className={`${GLASS_INPUT} tabular-nums`} data-testid="manual-utr-input" />
          </div>
        </div>

        {bonusPct > 0 && n >= minDep && (
          <div className="flex items-center justify-between rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/30 px-3 py-2 text-xs" data-testid="manual-bonus-preview">
            <span className="text-emerald-300 font-semibold">You'll receive</span>
            <span className="text-white font-bold tabular-nums">{inr(n + n * bonusPct / 100)} <span className="text-emerald-300/80 font-medium">(+{bonusPct}% bonus)</span></span>
          </div>
        )}

        <Cta onClick={onSubmit} disabled={!valid} testid="manual-submit-btn">Submit <ArrowRight className="h-5 w-5" /></Cta>
        <p className="text-[11px] text-slate-500 text-center">Admin verifies your payment · balance + {bonusPct}% bonus credited after approval.</p>
      </div>
    </div>
  );
}
